import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { useAuthUser } from "../lib/useAuthUser";
import { useVendorFlags } from "../lib/useVendorFlags";
import { startGoogleLogin, vendorClaimStart, vendorClaimVerify, vendorCorrection, claimInviteInfo, vendorClaimInvite, API } from "../lib/api";
import axios from "axios";
import StarterUpgrade from "../components/StarterUpgrade";

export default function Claim() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const invite = sp.get("invite");
  const returnPath = invite ? `/claim/${slug}?invite=${invite}` : `/claim/${slug}`;
  const [inv, setInv] = useState(null);
  const { user, loading: authLoading } = useAuthUser();
  const { flags, loading: flagsLoading } = useVendorFlags();
  const [tool, setTool] = useState(null);
  const [claim, setClaim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    axios.get(`${API}/tools/by-slug/${slug}`).then((r) => setTool(r.data)).catch(() => setTool(false));
  }, [slug]);

  useEffect(() => {
    if (!invite) return;
    claimInviteInfo(invite).then(setInv).catch((e) => setInv({ valid: false, detail: e?.response?.data?.detail || "this invite link is not valid" }));
  }, [invite]);

  useEffect(() => {
    if (invite && user && tool && inv?.valid && !claim) {
      vendorClaimInvite(invite)
        .then(setClaim)
        .catch((e) => setClaim({ status: "error", detail: e?.response?.data?.detail || "could not claim with this invite" }));
    }
  }, [invite, inv, user, tool, claim]);

  useEffect(() => {
    if (!invite && user && tool && flags.claiming && !claim) {
      vendorClaimStart(slug)
        .then(setClaim)
        .catch((e) => setClaim({ status: "error", detail: e?.response?.data?.detail || "could not start claim" }));
    }
  }, [invite, user, tool, flags.claiming, slug, claim]);

  const verify = async () => {
    setBusy(true);
    try {
      const r = await vendorClaimVerify(slug);
      if (r.status === "verified") {
        toast.success("verified — listing claimed");
        setClaim(r);
      } else toast.error(r.detail || "meta tag not found yet");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "verification failed");
    } finally {
      setBusy(false);
    }
  };

  const sendCorrection = async () => {
    setBusy(true);
    try {
      await vendorCorrection(slug, msg);
      toast.success("sent — we'll review it");
      setMsg("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not send");
    } finally {
      setBusy(false);
    }
  };

  if (flagsLoading || tool === null || (invite && inv === null)) return <Shell title="claim listing"><p className="text-[13px] text-[#888]">loading…</p></Shell>;
  if (invite && inv && !inv.valid && !(tool && tool.claimed) && claim?.status !== "verified")
    return (
      <Shell title="invite link" noindex>
        <p className="text-[13px]" data-testid="claim-invite-invalid">{inv.detail}. Reply to the email you received and we&apos;ll send a fresh one.</p>
      </Shell>
    );
  if ((!flags.claiming && !invite) || tool === false)
    return (
      <Shell title="not found" noindex>
        <p className="text-[13px]" data-testid="claim-not-found">that page isn't available.</p>
      </Shell>
    );

  const verified = claim?.status === "verified";
  return (
    <Shell title={`claim ${tool.title}`} noindex>
      <h1 className="text-xl font-bold border-b border-black pb-2" data-testid="claim-title">
        {">"}_ claim {tool.title}
      </h1>
      {invite && !verified && (
        <p className="text-[13px] border border-black p-2 mt-3" data-testid="claim-invite-banner">
          You were invited to claim this listing. Sign in with any Google account and it&apos;s yours — no domain check.
        </p>
      )}
      <p className="text-[13px] text-[#444] mt-3 leading-relaxed">
        Claiming attaches you as the owner of{" "}
        <Link to={`/tool/${tool.slug}`} className="tv-link">/tool/{tool.slug}</Link>.{" "}
        <b>It&apos;s free</b>: you get the ✓ claimed mark and can request corrections to keep your listing accurate.
        An expanded listing page &mdash; your own button, screenshots, your pitch and a pricing table &mdash; comes with the
        paid <b>Starter</b> upgrade, which you can add after claiming.
      </p>

      <div className="mt-4 border border-black p-3 text-[13px] bg-[#FBF8EE]" data-testid="claim-example-callout">
        <b>Curious what the paid Starter listing looks like?</b> See a fully built example:{" "}
        <Link to="/tool/edgarreader" className="tv-link" data-testid="claim-example-link">
          the EdgarReader example &rarr;
        </Link>{" "}
        &mdash; positioning in the maker&apos;s words, screenshots, a custom button and a full pricing table.
        The free claim doesn&apos;t include these; it&apos;s the ownership mark and corrections.
      </div>

      {authLoading ? null : !user ? (
        <div className="mt-6" data-testid="claim-login">
          <p className="text-[13px] mb-2">Sign in with the Google account you use at work — if its domain matches the tool&apos;s website you&apos;re verified instantly.</p>
          <button type="button" className="tv-btn tv-btn-primary" onClick={() => startGoogleLogin(returnPath)} data-testid="claim-login-btn">
            [sign in with Google]
          </button>
        </div>
      ) : verified ? (
        <div className="mt-6 space-y-6" data-testid="claim-next-steps">
          <div className="border border-black p-3 text-[13px]" data-testid="claim-verified">
            ✓ <b>{tool.title}</b> is claimed by {user.email}. See it in your{" "}
            <a href="/vendor" className="tv-link">vendor dashboard</a>.
          </div>

          {flags.starter && <StarterUpgrade slug={tool.slug} plan={tool.plan} />}

          {flags.sponsored && (
            <section className="border-2 border-black p-4" data-testid="claim-advertise">
              <div className="inline-block bg-[#ffff00] px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide">step · advertise</div>
              <h2 className="text-[15px] font-bold mt-2">Get {tool.title} in front of more buyers</h2>
              <p className="text-[13px] text-[#444] mt-1 leading-relaxed">
                Promote your tool in <b>sponsored slots</b> on the homepage and category pages — clearly labelled,
                and they never change the organic ranking.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a href="/vendor" className="tv-btn tv-btn-primary" data-testid="claim-advertise-dashboard">[advertise from your dashboard]</a>
                <Link to="/for-vendors" className="tv-btn" data-testid="claim-advertise-pricing">[see advertising &amp; pricing]</Link>
              </div>
            </section>
          )}

          <section data-testid="claim-correction">
            <h2 className="text-[15px] font-bold border-b border-[#ddd] pb-1">request a correction</h2>
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder="what's wrong and what it should say…"
              className="mt-2 w-full border border-black p-2 text-[13px] font-mono" data-testid="claim-correction-text" />
            <button type="button" className="tv-btn mt-2" disabled={busy || msg.trim().length < 3} onClick={sendCorrection} data-testid="claim-correction-send">
              [send]
            </button>
          </section>
        </div>
      ) : claim?.status === "pending" ? (
        <div className="mt-6 text-[13px] space-y-3" data-testid="claim-pending">
          <p>
            Your email ({user.email}) isn&apos;t on <b>{claim.domain}</b>, so prove you control the site: add this tag inside{" "}
            <code>&lt;head&gt;</code> on <b>{tool.url}</b>, deploy, then click verify.
          </p>
          <pre className="border border-black p-2 overflow-x-auto text-[12px]" data-testid="claim-meta-tag">{claim.meta_tag}</pre>
          <button type="button" className="tv-btn tv-btn-primary" disabled={busy} onClick={verify} data-testid="claim-verify-btn">
            {busy ? "checking…" : "[verify now]"}
          </button>
          <p className="text-[#666]">Prefer to sign in with a work email instead? <button type="button" className="tv-link underline" onClick={() => startGoogleLogin(returnPath)}>switch account</button></p>
        </div>
      ) : claim?.status === "error" ? (
        <p className="mt-6 text-[13px] text-[#b91c1c]" data-testid="claim-error">{claim.detail}</p>
      ) : (
        <p className="mt-6 text-[13px] text-[#888]">checking…</p>
      )}
    </Shell>
  );
}

function Shell({ title, noindex = true, children }) {
  return (
    <div className="min-h-screen bg-white text-black font-mono">
      <Seo title={title} description="Claim your listing on TerminalValue.io" path="/claim" noindex={noindex} />
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-10" data-testid="claim-page">{children}</main>
      <Footer />
    </div>
  );
}
